import {
    getTicketTransferErrorCode,
    MEMBERSHIP_TRANSFER_IN_PROGRESS_ERROR_CODE,
    SETTLEMENT_BULK_IN_PROGRESS_ERROR_CODE,
    TICKET_TRANSFER_IDEMPOTENCY_INDEX_MISSING_ERROR_CODE,
    TICKET_TRANSFER_OPERATION_IN_PROGRESS_ERROR_CODE,
    TICKET_TRANSFER_OPERATION_STALE_ERROR_CODE,
    TICKET_TRANSFER_SOURCE_READ_FAILED_ERROR_CODE,
    TicketTransferFailure,
    getTicketTransferRollbackState,
} from './errors';
import { TransferTicketBody } from './types';

/**
 * 양도 요청의 멱등 키(clientOperationId) — BACKEND_HANDOFF_N §3.
 *
 * - 입력 내용(원본·받는 회원·강사·회차·번호 정책·메모)이 바뀌면 **새 키**.
 * - 응답을 못 받았거나 "아직 처리 중" 일 때만 **같은 키**로 다시 보낸다. 그래야 응답이 유실된
 *   첫 요청이 이미 처리됐을 때 서버가 다시 쓰지 않고 첫 결과를 돌려준다(회차 이중 이동 방지).
 * - 서버가 확정 실패를 알려 준 뒤에는 같은 키를 쓰면 안 된다(OPERATION_FAILED 로 계속 거절된다).
 *
 * 그룹 수강권(MEMBER_TICKET)은 서버가 키를 400 으로 거부하므로 절대 싣지 않는다.
 */

const KEY_PATTERN = /^[A-Za-z0-9._:-]{8,128}$/;

export const isValidClientOperationId = (value: unknown): value is string =>
    typeof value === 'string' && KEY_PATTERN.test(value);

const randomHex = (length: number): string => {
    const cryptoApi: Crypto | undefined = typeof globalThis !== 'undefined' ? (globalThis as any).crypto : undefined;
    if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
        const bytes = new Uint8Array(Math.ceil(length / 2));
        cryptoApi.getRandomValues(bytes);
        return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('').slice(0, length);
    }
    let out = '';
    while (out.length < length) out += Math.random().toString(16).slice(2);
    return out.slice(0, length);
};

/** 서버 형식(8~128자, `[A-Za-z0-9._:-]`)에 맞는 새 키. */
export const createClientOperationId = (): string => {
    const cryptoApi: any = typeof globalThis !== 'undefined' ? (globalThis as any).crypto : undefined;
    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
        const id = cryptoApi.randomUUID();
        if (isValidClientOperationId(id)) return id;
    }
    return `tt-${Date.now().toString(36)}-${randomHex(24)}`;
};

/** 키가 붙을 수 있는 요청인가(PT 만). */
export const supportsClientOperationId = (body: Pick<TransferTicketBody, 'sourceType'>): boolean =>
    body.sourceType === 'MEMBERSHIP';

/**
 * 입력 내용의 지문. 키를 재사용해도 되는지(=내용이 그대로인지) 판정하는 데만 쓴다.
 * 키 자체는 지문에 넣지 않는다.
 */
export const makeTransferOperationFingerprint = (
    input: Pick<TransferTicketBody, 'sourceId' | 'toMemberId' | 'transferCount' | 'targetSessionNumberPolicy'> & {
        toCoachId?: string | null;
        memo?: string;
    },
): string =>
    JSON.stringify([
        input.sourceId || '',
        input.toMemberId || '',
        input.toCoachId || '',
        Number(input.transferCount) || 0,
        input.targetSessionNumberPolicy || 'RESET',
        input.memo || '',
    ]);

/** 응답을 못 받았거나 처리 중이라 같은 키로 다시 확인해야 하는 요청. */
export type PendingTransferOperation = {
    fingerprint: string;
    clientOperationId: string;
    /** 보냈던 본문 그대로(키 포함). 재시도는 이 본문을 다시 보낸다. */
    body: TransferTicketBody;
};

/** 지금 입력이 보류 중인 요청과 같은 내용이면 그 요청을 돌려준다. 다르면 null(=새 키). */
export const findReusablePendingOperation = (
    pending: PendingTransferOperation | null | undefined,
    fingerprint: string,
): PendingTransferOperation | null => (pending && pending.fingerprint === fingerprint ? pending : null);

/**
 * 본문에 키를 붙인다. 보류 중인 같은 내용의 요청이 있으면 그 키를, 없으면 새 키를 쓴다.
 * 그룹 수강권은 키 없이 그대로 돌려준다.
 */
export const attachClientOperationId = (
    body: TransferTicketBody,
    pending: PendingTransferOperation | null | undefined,
    create: () => string = createClientOperationId,
): TransferTicketBody => {
    if (!supportsClientOperationId(body)) {
        const { clientOperationId: _omit, ...rest } = body;
        return rest;
    }
    const fingerprint = makeTransferOperationFingerprint(body);
    const reusable = findReusablePendingOperation(pending, fingerprint);
    return { ...body, clientOperationId: reusable?.clientOperationId ?? create() };
};

const readStatus = (failure: TicketTransferFailure | null | undefined): number => {
    const status = failure?.status;
    return typeof status === 'number' && Number.isFinite(status) ? status : 0;
};

/**
 * 이 실패 뒤 다시 누를 때 **같은 키**를 써야 하는가.
 *
 * - 응답 자체가 없음(네트워크 끊김·시간 초과): 첫 요청이 처리됐을 수 있다 → 같은 키.
 * - 409 `TICKET_TRANSFER_OPERATION_IN_PROGRESS`(이 키를 처리 중): 끝난 뒤 재생 응답을 받는다 → 같은 키.
 *   이 코드는 서버 멱등 인덱스가 살아 있을 때만 나온다.
 * - 409 `MEMBERSHIP_TRANSFER_IN_PROGRESS`(같은 원본의 **다른** 양도 처리 중)는 여기 들지 않는다.
 *   기존 코드라 인덱스가 없어도 나오고, 그때 서버는 키를 무시한다 — 같은 키 재전송이 안전하지 않다.
 * - 5xx(503 인덱스 미준비·503 원본 다시 읽기 실패 제외): 게이트웨이 시간 초과처럼 서버가 처리했는지 모른다 → 같은 키.
 *   서버가 실패로 끝냈다면 같은 키에 OPERATION_FAILED 가 와서 그때 새 키로 넘어간다.
 * - 나머지(4xx 확정 실패, 503 인덱스 미준비, 503 원본 다시 읽기 실패): 새 키.
 *   (상품 분류 일괄 적용 중 409 는 첫 제출이면 화면이 같은 내용 재제출에 한해 키를 이어 쓴다 — classifySameKeyFailure)
 */
export const shouldReuseOperationKeyAfterFailure = (
    failure: TicketTransferFailure | null | undefined,
): boolean => {
    const status = readStatus(failure);
    if (status === 0) return true;
    const errorCode = getTicketTransferErrorCode(failure);
    if (status === 409) {
        return errorCode === TICKET_TRANSFER_OPERATION_IN_PROGRESS_ERROR_CODE;
    }
    if (status >= 500) {
        if (isTicketTransferBusyRejection(failure)) return false;
        return !(status === 503 && errorCode === TICKET_TRANSFER_IDEMPOTENCY_INDEX_MISSING_ERROR_CODE);
    }
    return false;
};

/**
 * 상품 분류 일괄 적용과 겹쳐 거절된 실패(서버 10/18 H2·I3).
 *  - 503 `TICKET_TRANSFER_SOURCE_READ_FAILED` — 잠금 안에서 원본 수강권을 다시 읽지 못했다. 사가 기록(첫 쓰기) 전이라
 *    **이 요청은 아무것도 쓰지 않았다**(서버 ticketTransfer.service transferMembership).
 *  - 409 `SETTLEMENT_BULK_IN_PROGRESS` — 대부분 잠금을 못 잡아 쓰기 전에 거절된 것이지만, **대상 생성 뒤 마지막 잠금 확인
 *    (assertTemplateLock → renewLock)에서 잠금을 잃어도 같은 코드로 온다.** 그때 서버는 원본·대상을 되돌리고 사가를 FAILED 로
 *    남기지만 되돌리기 실패는 삼킨다 — 「아무것도 안 씀」을 보장하지 않는다(10/19 교차검증 지적 A).
 * 상태 코드와 오류 코드가 둘 다 맞을 때만 그렇게 본다(프록시 503 등 본문 없는 5xx 는 여전히 응답 유실로 다룬다).
 */
export const isTicketTransferBusyRejection = (failure: TicketTransferFailure | null | undefined): boolean => {
    const status = readStatus(failure);
    const errorCode = getTicketTransferErrorCode(failure);
    return (
        (status === 409 && errorCode === SETTLEMENT_BULK_IN_PROGRESS_ERROR_CODE) ||
        (status === 503 && errorCode === TICKET_TRANSFER_SOURCE_READ_FAILED_ERROR_CODE)
    );
};

/**
 * 같은 키 **자동** 재시도 상한(BACKEND_HANDOFF_N §3): 최대 3회, 2초·4초·8초 간격(총 약 15초).
 *
 * 자동 재시도는 409 `TICKET_TRANSFER_OPERATION_IN_PROGRESS` **하나**에서만 한다. 이 코드는 서버 멱등
 * 인덱스가 살아 있을 때만 나오므로 같은 키 재전송이 안전하다.
 * `MEMBERSHIP_TRANSFER_IN_PROGRESS`(같은 수강권의 다른 양도 처리 중)는 인덱스가 없어도 나오는 기존 코드다.
 * 그때 서버는 키를 무시하므로, 응답 유실 뒤 그 409 를 받고 자동 재전송하면 앞 요청이 끝난 다음 두 번째
 * 양도가 된다(회차 이중 이동). 그래서 자동 재시도하지 않는다. 응답 유실·5xx 도 같은 이유로 자동 재전송 안 함.
 */
export const SAME_KEY_RETRY_DELAYS_MS = [2000, 4000, 8000] as const;
export const MAX_SAME_KEY_RETRIES = SAME_KEY_RETRY_DELAYS_MS.length;

/** `retriesUsed` 번 재시도한 뒤 다음 재시도까지 기다릴 시간. 상한에 닿았으면 null. */
export const getSameKeyRetryDelayMs = (retriesUsed: number): number | null =>
    Number.isInteger(retriesUsed) && retriesUsed >= 0 && retriesUsed < MAX_SAME_KEY_RETRIES
        ? SAME_KEY_RETRY_DELAYS_MS[retriesUsed]
        : null;

/**
 * 키를 실은 요청이 실패한 뒤 할 일.
 *
 * - `RETRY_SAME_KEY`: 「처리 중」 409 — 기다렸다가 같은 키·같은 본문으로 자동 재확인.
 * - `ASK_MANUAL_RETRY`: 응답 유실·5xx — 다시 보내지 않는다. 데이터를 다시 불러와 잔여를 보게 하고,
 *   사용자가 직접 누를 때만 같은 키로 1회 재전송한다.
 * - `UNRESOLVED`: 처리됐는지 알 수 없는데 더 확인할 수 없다(자동 상한 도달, 수동 재전송도 응답 유실·5xx,
 *   서버가 10분 넘게 멈춘 기록이라고 알려 준 STALE). **새 키로 다시 보내지 않는다.**
 * - `BUSY_KEEP_KEY`: 첫 제출이 상품 분류 일괄 적용과 겹쳐 거절됐다(isTicketTransferBusyRejection). 409 는 드물게 쓰기 뒤에도
 *   오므로 키를 버리지 않는다 — 화면은 같은 키·같은 본문을 대기 저장소에 남겨 창을 다시 열어도 그 요청으로만 다시 보내게 한다
 *   (응답 유실 흐름과 같은 재오픈 동작). 멱등 인덱스가 살아 있으면 서버가 앞 요청의 기록(FAILED 등)을 재생해 두 번째 양도를 막고,
 *   쓰기 전 거절이었다면 기록이 없어 같은 키 재전송이 정상 처리된다. 그 재전송은 수동 재확인으로 치지 않는다.
 * - `ROLLBACK_INCOMPLETE`: 실패 재생(OPERATION_FAILED)이 되돌리기가 끝나지 않았다고 알려 줬다(rollbackComplete false).
 *   새 키로 다시 내면 중복 발급이 될 수 있다 — 이 수강권 제출을 막고(같은 키 기억은 유지) 확인을 안내한다.
 * - `NEW_KEY`: 서버가 확정 실패를 알려 줬다. 다음 제출은 새 키(되돌리기 결과를 모르는 옛 서버면 확인 경고와 함께).
 */
export type SameKeyFailureOutcome =
    | 'RETRY_SAME_KEY'
    | 'ASK_MANUAL_RETRY'
    | 'BUSY_KEEP_KEY'
    | 'ROLLBACK_INCOMPLETE'
    | 'UNRESOLVED'
    | 'NEW_KEY';

export const isTicketTransferStaleFailure = (failure: TicketTransferFailure | null | undefined): boolean =>
    getTicketTransferErrorCode(failure) === TICKET_TRANSFER_OPERATION_STALE_ERROR_CODE;

/** 이 키의 요청을 서버가 아직 처리 중(409). 자동 재확인이 허용되는 유일한 경우. */
export const isTicketTransferInProgressFailure = (failure: TicketTransferFailure | null | undefined): boolean =>
    readStatus(failure) === 409 &&
    getTicketTransferErrorCode(failure) === TICKET_TRANSFER_OPERATION_IN_PROGRESS_ERROR_CODE;

/** 같은 수강권의 다른 양도를 처리 중(409, 기존 코드). 자동 재시도 금지 — 안내만. */
export const isOtherTransferInProgressFailure = (failure: TicketTransferFailure | null | undefined): boolean =>
    readStatus(failure) === 409 &&
    getTicketTransferErrorCode(failure) === MEMBERSHIP_TRANSFER_IN_PROGRESS_ERROR_CODE;

export const classifySameKeyFailure = (
    failure: TicketTransferFailure | null | undefined,
    {
        autoRetriesUsed,
        isManualRetry,
    }: {
        /** 이번 제출에서 이미 한 자동 재시도 횟수. */
        autoRetriesUsed: number;
        /** 이번 제출이 사용자가 누른 「같은 요청 다시 확인」(수동 1회)인가. */
        isManualRetry: boolean;
    },
): SameKeyFailureOutcome => {
    if (isTicketTransferStaleFailure(failure)) return 'UNRESOLVED';
    // 실패 재생: 되돌리기가 끝나지 않았다고 확인된 경우만 막는다. 'complete'·'unknown' 은 아래로 내려가 NEW_KEY.
    if (getTicketTransferRollbackState(failure) === 'incomplete') return 'ROLLBACK_INCOMPLETE';
    if (isTicketTransferBusyRejection(failure)) {
        // 처음 제출: 안내만(응답 유실 흐름 아님), 같은 내용 재제출이면 같은 키.
        // 재확인 중(수동·자동): 이번 응답은 앞 요청의 결과를 알려 주지 않는다. 멱등 인덱스가 없는 서버는 키를 무시하므로
        // 또 보내게 두면 두 번째 양도가 될 수 있다 → 다른 양도 처리 중(MEMBERSHIP_TRANSFER_IN_PROGRESS)과 같이 결과 미확인으로 멈춘다.
        return isManualRetry || autoRetriesUsed > 0 ? 'UNRESOLVED' : 'BUSY_KEEP_KEY';
    }
    if (isTicketTransferInProgressFailure(failure)) {
        return getSameKeyRetryDelayMs(autoRetriesUsed) === null ? 'UNRESOLVED' : 'RETRY_SAME_KEY';
    }
    if (isOtherTransferInProgressFailure(failure)) {
        // 처음 제출에서 받았다면 우리 요청은 처리된 적이 없다 → 안내만 하고 다음 제출은 새 키.
        // 수동 재확인·자동 재확인 중에 받았다면 앞서 보낸 우리 요청 자체가 처리 중일 수 있다
        // (인덱스 없이 키가 무시되는 서버) → 결과 미확인으로 멈춘다. 어느 쪽도 자동 재전송하지 않는다.
        return isManualRetry || autoRetriesUsed > 0 ? 'UNRESOLVED' : 'NEW_KEY';
    }
    if (shouldReuseOperationKeyAfterFailure(failure)) {
        // 응답 유실·5xx. 수동 재전송은 1회뿐이다.
        return isManualRetry ? 'UNRESOLVED' : 'ASK_MANUAL_RETRY';
    }
    return 'NEW_KEY';
};

/**
 * 구서버(키를 모르는 검증)가 `clientOperationId` 를 모르는 필드로 거부한 400 인가.
 * Joi 기본값이 모르는 키를 거부해 `"clientOperationId" is not allowed` 가 온다. 검증 단계 거부라
 * 서버는 아무것도 쓰지 않았다 — 키를 빼고 한 번 다시 보내도 안전하다.
 */
export const isClientOperationIdRejected = (failure: TicketTransferFailure | null | undefined): boolean => {
    if (readStatus(failure) !== 400) return false;
    if (getTicketTransferErrorCode(failure)) return false;
    const payload: any = (failure as any)?.body ?? (failure as any)?.data;
    const texts = [
        (failure as any)?.serverMessage,
        (failure as any)?.message,
        payload && typeof payload === 'object' ? payload.message : undefined,
    ];
    // 구서버 Joi 의 「모르는 키」 거부 문구로만 좁힌다(다른 400 문구에 이름이 섞여도 오판하지 않게).
    return texts.some((text) => typeof text === 'string' && text.includes('"clientOperationId" is not allowed'));
};

export const stripClientOperationId = (body: TransferTicketBody): TransferTicketBody => {
    const { clientOperationId: _omit, ...rest } = body;
    return rest;
};

/**
 * 이 세션에서 서버가 키를 거부한 적이 있는가. 한 번 거부되면 이후 요청은 키 없이 보낸다
 * (매번 400 → 재전송을 반복하지 않게). 표시는 **10분 뒤 만료**된다 — 그 사이 신서버로 바뀌면
 * 다시 키를 싣는다. 저장소 접근이 막혀도 메모리 값으로 동작한다.
 */
const KEY_UNSUPPORTED_STORAGE = 'rappo:ticketTransfer:clientOperationIdUnsupported';
export const KEY_UNSUPPORTED_TTL_MS = 10 * 60 * 1000;
let keyUnsupportedAt: number | null = null;

export const markClientOperationIdUnsupported = (now: number = Date.now()) => {
    keyUnsupportedAt = now;
    try {
        window.sessionStorage.setItem(KEY_UNSUPPORTED_STORAGE, String(now));
    } catch {
        // 무시 — 메모리 값으로 충분하다.
    }
};

export const isClientOperationIdUnsupported = (now: number = Date.now()): boolean => {
    let markedAt = keyUnsupportedAt;
    if (markedAt === null) {
        try {
            const raw = window.sessionStorage.getItem(KEY_UNSUPPORTED_STORAGE);
            markedAt = raw && /^\d+$/.test(raw) ? Number(raw) : null;
        } catch {
            markedAt = null;
        }
    }
    return markedAt !== null && now - markedAt >= 0 && now - markedAt < KEY_UNSUPPORTED_TTL_MS;
};

/** 테스트 전용 */
export const __resetClientOperationIdSupportForTest = () => {
    keyUnsupportedAt = null;
    try {
        window.sessionStorage.removeItem(KEY_UNSUPPORTED_STORAGE);
    } catch {
        // 무시
    }
};
